/**
 * Guards LDS-backed order listing fetches (getLDSOrdersByAgentId, getDynamicOrdersByAgentId).
 * Order history is open to every authenticated agent; commercial search entitlements apply
 * only to new search/booking flows, not to viewing existing orders.
 */

import { isAdminUser } from "../../services/authService";

function devTraceEnabled() {
  try {
    if (import.meta.env?.DEV) return true;
  } catch {
    /* ignore */
  }
  return typeof process !== "undefined" && process.env.NODE_ENV === "development";
}

/** @param {Record<string, unknown>} entry */
export function logOrdersEntitlementFetchGuardTrace(entry) {
  if (!devTraceEnabled()) return;
  // eslint-disable-next-line no-console
  console.log("[ORDERS_ENTITLEMENT_FETCH_GUARD_TRACE]", entry);
}

/**
 * Deal types accepted by POST /getLDSOrdersByAgentId
 * @typedef {"FlightsHotel"|"OrganizedTour"|"Flights"|"Flight"} LdsDealTypeApi
 */

/**
 * @param {object|null|undefined} selectors — booleans from useAgentEntitlements
 * @param {string} dealType
 * @param {{ adminBypass?: boolean }} [opts]
 */
export function canFetchOrdersForDealType(selectors, dealType, opts = {}) {
  if (opts.adminBypass === true) return true;
  if (isAdminUser()) return true;
  void selectors;
  void dealType;
  return true;
}

/**
 * POST /getDynamicOrdersByAgentId — dynamic packages (“טיסות + מלון” דינמי)
 *
 * @param {object|null|undefined} selectors
 * @param {{ adminBypass?: boolean }} [opts]
 */
export function canFetchDynamicOrdersList(selectors, opts = {}) {
  if (opts.adminBypass === true) return true;
  if (isAdminUser()) return true;
  void selectors;
  return true;
}

/**
 * Keys used for auditing / traces (commercial matrix keys).
 * @param {string} dealType
 * @returns {string}
 */
export function requiredEntitlementKeysForLdsDealType(dealType) {
  const d = String(dealType ?? "").trim();
  if (d === "Flights" || d === "Flight") return "search_flights";
  if (d === "OrganizedTour") return "organized_tours";
  if (d === "FlightsHotel" || d === "DynamicPackage") {
    return "search_packages|dynamic_packages";
  }
  if (d === "VacationPackage") return "search_packages";
  return "(unknown)";
}

/**
 * @param {string} dealType
 * @param {{
 *   selectors?: Record<string, boolean>|null,
 *   entitlementsStatus?: string|null,
 *   adminBypass?: boolean,
 *   userId?: unknown,
 *   role?: string|null,
 * }} [ctx]
 * @param {string} endpointLabel — logged URL substring
 */
export function resolveLdsOrdersFetchDecision(dealType, ctx = {}, endpointLabel = "") {
  const normalizedType = String(dealType ?? "").trim();
  const requiredFeature = requiredEntitlementKeysForLdsDealType(normalizedType);
  let reason = "";
  let allowed = false;
  let requestSkipped = false;

  if (ctx.adminBypass === true || isAdminUser()) {
    allowed = true;
    reason = isAdminUser() ? "admin_or_super_role" : "explicit_adminBypass";
    logOrdersEntitlementFetchGuardTrace({
      userId: ctx.userId ?? null,
      role: ctx.role ?? null,
      dealType: normalizedType,
      requiredFeature,
      allowed: true,
      requestSkipped: false,
      endpoint: endpointLabel,
      reason,
    });
    return {
      skipFetch: false,
      allowed: true,
      requiredFeature,
      requestSkipped: false,
      dealType: normalizedType,
      reason,
    };
  }

  const st =
    ctx.entitlementsStatus != null ? String(ctx.entitlementsStatus).trim() : "";
  if (
    (st === "loading" || st === "idle") &&
    ctx.adminBypass !== true &&
    !isAdminUser()
  ) {
    requestSkipped = true;
    reason = "entitlements_not_ready";
    logOrdersEntitlementFetchGuardTrace({
      userId: ctx.userId ?? null,
      role: ctx.role ?? null,
      dealType: normalizedType,
      requiredFeature,
      allowed: false,
      requestSkipped: true,
      endpoint: endpointLabel,
      reason,
    });
    return {
      skipFetch: true,
      allowed: false,
      requiredFeature,
      requestSkipped: true,
      dealType: normalizedType,
      reason,
    };
  }

  allowed = canFetchOrdersForDealType(ctx.selectors, normalizedType);
  requestSkipped = !allowed;
  reason = allowed ? "entitlement_ok" : "missing_required_entitlement";

  logOrdersEntitlementFetchGuardTrace({
    userId: ctx.userId ?? null,
    role: ctx.role ?? null,
    dealType: normalizedType,
    requiredFeature,
    allowed,
    requestSkipped,
    endpoint: endpointLabel,
    reason,
  });

  return {
    skipFetch: !allowed,
    allowed,
    requiredFeature,
    requestSkipped,
    dealType: normalizedType,
    reason,
  };
}

/**
 * @param {{
 *   selectors?: Record<string, boolean>|null,
 *   entitlementsStatus?: string|null,
 *   adminBypass?: boolean,
 *   userId?: unknown,
 *   role?: string|null,
 * }} [ctx]
 * @param {string} endpointLabel
 */
export function resolveDynamicOrdersFetchDecision(ctx = {}, endpointLabel = "") {
  const requiredFeature = "dynamic_packages";
  if (ctx.adminBypass === true || isAdminUser()) {
    logOrdersEntitlementFetchGuardTrace({
      userId: ctx.userId ?? null,
      role: ctx.role ?? null,
      dealType: "dynamic_orders_list",
      requiredFeature,
      allowed: true,
      requestSkipped: false,
      endpoint: endpointLabel,
      reason: isAdminUser() ? "admin_or_super_role" : "explicit_adminBypass",
    });
    return {
      skipFetch: false,
      allowed: true,
      requiredFeature,
      requestSkipped: false,
      reason: "admin_or_bypass",
    };
  }

  const st =
    ctx.entitlementsStatus != null ? String(ctx.entitlementsStatus).trim() : "";
  if (
    (st === "loading" || st === "idle") &&
    ctx.adminBypass !== true &&
    !isAdminUser()
  ) {
    logOrdersEntitlementFetchGuardTrace({
      userId: ctx.userId ?? null,
      role: ctx.role ?? null,
      dealType: "dynamic_orders_list",
      requiredFeature,
      allowed: false,
      requestSkipped: true,
      endpoint: endpointLabel,
      reason: "entitlements_not_ready",
    });
    return {
      skipFetch: true,
      allowed: false,
      requiredFeature,
      requestSkipped: true,
      reason: "entitlements_not_ready",
    };
  }

  const ok = canFetchDynamicOrdersList(ctx.selectors);
  logOrdersEntitlementFetchGuardTrace({
    userId: ctx.userId ?? null,
    role: ctx.role ?? null,
    dealType: "dynamic_orders_list",
    requiredFeature,
    allowed: ok,
    requestSkipped: !ok,
    endpoint: endpointLabel,
    reason: ok ? "entitlement_ok" : "missing_dynamic_packages",
  });
  return {
    skipFetch: !ok,
    allowed: ok,
    requiredFeature,
    requestSkipped: !ok,
    reason: ok ? "entitlement_ok" : "missing_dynamic_packages",
  };
}

/**
 * Axios-shaped body for callers expecting `response.data.orders`
 *
 * @param {number|string|null|undefined} page
 * @param {number|string|null|undefined} size
 */
export function emptyAxiosOrdersListEnvelope(page, size) {
  const p = Number(page) > 0 ? Number(page) : 1;
  const sz = Number(size) > 0 ? Number(size) : 0;
  return {
    status: 200,
    data: {
      orders: [],
      total: 0,
      page: p,
      page_size: sz,
    },
  };
}
